/**
 * /accept-invite?token=… — redeem an org invite (multi-tenancy Phase F,
 * Slice 7). Matches the link emailed by CreateInvite.
 *
 * Mounted under RequireAuth but OUTSIDE the /app RequireOrg guard, so a
 * brand-new invitee with zero orgs can accept (RequireOrg would otherwise
 * bounce them to /onboarding first). On success we set the joined org active
 * and hard-reload into /app so all data loads scoped to it.
 *
 * Bug #119 — surviving the sign-in round-trip: a signed-out invitee who clicks
 * the link is bounced by RequireAuth to /login, and in OIDC mode the SPA
 * navigates the window cross-origin to Zitadel and back via /auth/callback →
 * /app, dropping the `?token=` entirely. So the FIRST thing we do here is
 * stash the token in sessionStorage (lib/pending-invite). That lets RequireOrg
 * re-route a freshly-signed-in zero-org user back to /accept-invite to redeem
 * it — instead of dumping them into the new-org onboarding flow. The token is
 * cleared once redeemed (or on a terminal error) so a stale/bad token can't
 * trap the user in a redirect.
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";

import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { invitesApi } from "@/lib/api";
import { ApiClientError } from "@/lib/api-client";
import { clearInviteToken } from "@/lib/pending-invite";

function inviteErrorMessage(e: unknown): string {
  if (e instanceof ApiClientError) {
    const code = (e.body as { error?: string } | null)?.error;
    if (code === "invite_not_found") return "This invite link is invalid.";
    if (code === "invite_expired") return "This invite has expired.";
    if (code === "invite_not_pending")
      return "This invite has already been used or was revoked.";
  }
  return "Couldn't accept this invite. Please try again.";
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setActiveOrg } = useOrg();
  const toast = useToast();
  const firedRef = useRef(false);
  const [errored, setErrored] = useState(false);

  // The token is stashed to sessionStorage by <StashInviteToken> in App.tsx —
  // above RequireAuth — so it survives the sign-in round-trip even for a
  // signed-out invitee (this component wouldn't mount in that case). Here we
  // just redeem it and clear the stash on settle.
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    if (!token) {
      clearInviteToken();
      setErrored(true);
      return;
    }

    void (async () => {
      try {
        const org = await invitesApi.accept(token);
        // Redeemed — drop the stashed token so it can't re-trigger a redirect.
        clearInviteToken();
        // Persist the joined org as active, then hard-reload into /app so
        // every request resolves under it (the reload re-fetches /auth/me,
        // which now includes this org, so the OrgProvider keeps it).
        setActiveOrg(org.id);
        toast.push("ok", `You've joined ${org.name}.`);
        window.location.assign("/app");
      } catch (e) {
        // Terminal failure (invalid / expired / already-used): clear the token
        // so RequireOrg won't keep routing the user back here in a loop.
        clearInviteToken();
        toast.push("error", inviteErrorMessage(e));
        setErrored(true);
      }
    })();
  }, [token, setActiveOrg, toast]);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-surface px-6 text-center text-on-surface">
      {errored ? (
        <>
          <AlertTriangle className="size-8 text-danger" />
          <h1 className="text-lg font-semibold">Invite couldn't be accepted</h1>
          <p className="text-sm text-on-surface-muted">
            The link may be invalid, expired, or already used.
          </p>
          <a className="text-sm text-brand underline" href="/app">
            Go to the app
          </a>
        </>
      ) : (
        <>
          <Loader2 className="size-7 animate-spin text-on-surface-muted" />
          <p className="text-sm text-on-surface-muted">Accepting your invite…</p>
        </>
      )}
    </div>
  );
}
